import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';
import './AddOnsPage.css'; // Your CSS file

const CoinIcon = () => (
  <svg className="coin-price-icon" width="24" height="24" viewBox="0 0 24 24">
    <defs>
      <linearGradient id="priceGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFD700" />
        <stop offset="100%" stopColor="#FFA500" />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="9" fill="url(#priceGold)" stroke="#DAA520" strokeWidth="1" />
    <path d="M12 6L13 10L17 11L13 12L12 16L11 12L7 11L11 10L12 6Z" fill="white" opacity="0.8" />
  </svg>
);

const AddOnsPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quantities, setQuantities] = useState({});
  const [cart, setCart] = useState([]);
  const [isCartVisible, setIsCartVisible] = useState(false); // ✅ New state
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [pendingItems, setPendingItems] = useState(new Set());

  const [user, setUser] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);

  // Fetch menu items
  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoading(true);
      try {
        const mealsSnapshot = await getDocs(collection(db, 'meals'));
        const items = mealsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMenuItems(items);

        const initialQuantities = {};
        items.forEach(item => { initialQuantities[item.id] = 1; });
        setQuantities(initialQuantities);
      } catch (err) {
        console.error("Error fetching menu: ", err);
        setError("Failed to load the menu. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchMenuItems();
  }, []);

  // Handle user auth + listen for pending add-on requests
  useEffect(() => {
    const auth = getAuth();
    let unsubscribeFirestore = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (unsubscribeFirestore) unsubscribeFirestore();

      if (currentUser) {
        setUser(currentUser);

        const customerQuery = query(collection(db, 'customer'), where('email', '==', currentUser.email));
        const customerSnapshot = await getDocs(customerQuery);

        if (!customerSnapshot.empty) {
          const customerDoc = customerSnapshot.docs[0];
          const profile = { id: customerDoc.id, ...customerDoc.data() };
          setCustomerProfile(profile);

          const q = query(
            collection(db, 'addon_requests'),
            where('customerId', '==', customerDoc.id)
          );

          unsubscribeFirestore = onSnapshot(q, (snapshot) => {
            const activeItemIds = new Set();
            snapshot.forEach(orderDoc => {
              const orderData = orderDoc.data();
              if (
                orderData.items &&
                Array.isArray(orderData.items) &&
                (orderData.status === 'pending' || orderData.status === 'approved')
              ) {
                orderData.items.forEach(item => activeItemIds.add(item.foodItemId));
              }
            });
            setPendingItems(activeItemIds);
          });
        } else {
          console.error("Could not find customer profile for email:", currentUser.email);
          setError("Your customer profile could not be found. Please contact support.");
          setCustomerProfile(null);
        }
      } else {
        setUser(null);
        setCustomerProfile(null);
        setPendingItems(new Set());
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) unsubscribeFirestore();
    };
  }, []);

  const handleQuantityChange = (itemId, delta) => {
    setQuantities(prev => ({ ...prev, [itemId]: Math.max(1, (prev[itemId] || 1) + delta) }));
  };

  const handleAddToCart = (item) => {
    const itemToAdd = { ...item, quantity: quantities[item.id] || 1 };
    setCart(prev => [...prev, itemToAdd]);
    setIsCartVisible(true); // ✅ Show summary again when adding items
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(prev => prev.filter(cartItem => cartItem.id !== itemId));
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0 || !user || !customerProfile) {
      alert("You must be logged in and have a valid customer profile to place an order.");
      return;
    }

    setIsPlacingOrder(true);

    const totalDiscountCoinsEarned = cart.reduce((total, item) => {
      return total + (Number(item.discountCoins) || 0) * item.quantity;
    }, 0);

    try {
      const orderData = {
        customerId: customerProfile.id,
        customerName: customerProfile.name || 'N/A',
        customerEmail: customerProfile.email,
        items: cart.map(item => ({
          foodItemId: item.id,
          foodItemName: item.name,
          unitPrice: Number(item.coins) || 0,
          quantity: item.quantity,
          totalCoins: (Number(item.coins) || 0) * item.quantity,
          discountCoinsEarned: (Number(item.discountCoins) || 0) * item.quantity,
          mealType: item.mealType || 'Not specified',
        })),
        totalOrderCost: cart.reduce((total, item) => total + (Number(item.coins) || 0) * item.quantity, 0),
        totalDiscountCoinsEarned,
        status: 'pending',
        requestedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "addon_requests"), orderData);

      alert(`✅ Success! Your order has been submitted for approval.`);
      setCart([]);
      setIsCartVisible(false); // ✅ Hide after placing order
    } catch (err) {
      console.error("❌ Error placing order:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (loading) return <div className="loading-state">Loading menu...</div>;
  if (error) return <div className="error-message">{error}</div>;

  const totalCartCost = cart.reduce((total, item) => total + (Number(item.coins) || 0) * item.quantity, 0);

  return (
    <div className="menu-page-container">
      <div className="page-title-container">
        <h1>Add-on Meals</h1>
        <p>Select extra items to add to your subscription.</p>
      </div>

      <div className="menu-grid">
        {menuItems.map(item => {
          const isPending = pendingItems.has(item.id);
          const isInCart = cart.some(cartItem => cartItem.id === item.id);
          const currentQuantity = quantities[item.id] || 1;

          return (
            <div key={item.id} className={`menu-card ${isPending ? 'disabled' : ''}`}>
              <img src={item.imageUrl} alt={item.name} className="menu-card-image" />
              <div className="menu-card-content">
                <h3 className="menu-card-title">{item.name}</h3>

                {item.mealType && (
                  <div className="meal-type-badge">{item.mealType}</div>
                )}

                <p className="menu-card-category">{item.isPureVeg ? 'Pure Veg' : 'Non-Veg'}</p>

                {item.discountCoins > 0 && (
                  <div className="discount-coin-badge">
                    Earn {item.discountCoins} Coins
                  </div>
                )}
              </div>
              <div className="menu-card-footer">
                <div className="price-and-quantity">
                  <div className="coin-price">
                    <CoinIcon />
                    <span>{(Number(item.coins) || 0) * currentQuantity}</span>
                  </div>
                  <div className="quantity-selector">
                    <button
                      onClick={() => handleQuantityChange(item.id, -1)}
                      disabled={currentQuantity <= 1 || isInCart || isPending}
                    >-</button>
                    <span>{currentQuantity}</span>
                    <button
                      onClick={() => handleQuantityChange(item.id, 1)}
                      disabled={isInCart || isPending}
                    >+</button>
                  </div>
                </div>
                {isInCart ? (
                  <button className="cart-button remove" onClick={() => handleRemoveFromCart(item.id)}>Remove</button>
                ) : (
                  <button className="cart-button add" onClick={() => handleAddToCart(item)} disabled={isPending}>
                    {isPending ? 'Request Pending' : 'Add to Cart'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ Show cart only if visible & has items */}
      {isCartVisible && cart.length > 0 && (
        <div className="cart-summary-container">
          {/* Close just hides, not clear */}
          <button
            className="cart-close-btn"
            onClick={() => setIsCartVisible(false)}
            aria-label="Close Cart"
          >
            ×
          </button>

          <h3>Order Summary</h3>
          <div className="cart-items-list">
            {cart.map(item => (
              <div key={item.id} className="cart-item">
                <span className="cart-item-name">{item.name} (x{item.quantity})</span>
                <span className="cart-item-price">
                  <CoinIcon />
                  {(Number(item.coins) || 0) * item.quantity}
                </span>
              </div>
            ))}
          </div>
          <div className="cart-total">
            <strong>Total Cost</strong>
            <strong>
              <CoinIcon />
              {totalCartCost}
            </strong>
          </div>
          <button
            className="place-order-button"
            onClick={handlePlaceOrder}
            disabled={isPlacingOrder}
          >
            {isPlacingOrder ? 'Placing Order...' : `Place Order Request`}
          </button>
        </div>
      )}
    </div>
  );
};

export default AddOnsPage;
